import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, Users, ArrowRight, CheckCircle, PiggyBank, BarChart3 } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section 
        className="relative min-h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(6, 78, 59, 0.9), rgba(6, 78, 59, 0.85)), url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6" style={{letterSpacing: '-0.02em'}}>
            شركة أفيوس للتجارة والتوريدات العامة
          </h1>
          <p className="text-lg mb-4 opacity-90 max-w-3xl mx-auto">
            كيان تجاري حديث برؤية استثمارية واضحة، متخصص في إدارة عمليات البيع والشراء بكفاءة عالية
          </p>
          <p className="text-base mb-8 opacity-80 max-w-3xl mx-auto">
            نعمل كحلقة وصل موثوقة بين المصانع، تجار الجملة، المطاعم، والعملاء التجاريين
          </p>
          <Button
            data-testid="login-btn"
            onClick={() => navigate('/login')}
            size="lg"
            className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full px-8 py-6 text-lg font-bold backdrop-blur-sm"
          >
            تسجيل الدخول
          </Button>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-4 text-primary">مجالاتنا الرئيسية</h2>
          <p className="text-center text-muted-foreground mb-12 text-base lg:text-lg">نقدم حلولاً متكاملة في ثلاثة مجالات رئيسية</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2 border-stone-200 hover:border-primary transition-all duration-300 hover:-translate-y-2">
              <CardHeader>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <PiggyBank className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl font-bold">التجارة</CardTitle>
                <CardDescription className="text-base">
                  شراء وبيع المنتجات بين المصانع، تجار الجملة، والعملاء التجاريين بأفضل الأسعار التنافسية
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-stone-200 hover:border-accent transition-all duration-300 hover:-translate-y-2">
              <CardHeader>
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-4">
                  <BarChart3 className="h-8 w-8 text-accent" />
                </div>
                <CardTitle className="text-2xl font-bold">التوريدات</CardTitle>
                <CardDescription className="text-base">
                  توريد احتياجات المصانع، المطاعم، والشركات بجودة مضمونة والتزام كامل بالمواعيد
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-stone-200 hover:border-green-600 transition-all duration-300 hover:-translate-y-2">
              <CardHeader>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-2xl font-bold">الاستثمارات</CardTitle>
                <CardDescription className="text-base">
                  الدخول في شراكات واستثمارات تجارية قائمة على دراسات سوق حقيقية وتدفقات نقدية واضحة
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Invest Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-4 text-primary">لماذا تستثمر معنا؟</h2>
          <p className="text-center text-muted-foreground mb-12 text-base lg:text-lg">مميزات الاستثمار في أفيوس جروب</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'شفافية كاملة', desc: 'تتبع استثمارك بشكل مباشر ومعرفة الأرباح التراكمية' },
              { title: 'عوائد مستقرة', desc: 'أرباح منتظمة من عمليات تجارية حقيقية' },
              { title: 'سهولة المتابعة', desc: 'لوحة تحكم شاملة لمتابعة أرباحك' },
              { title: 'متابعة مستمرة', desc: 'تحديثات دورية لكل عملية في حسابك' },
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-md">
                <CheckCircle className="h-12 w-12 text-green-600 mb-4" />
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-6">تواصل معنا للاستثمار</h2>
          <p className="text-lg mb-8 opacity-90">
            انضم إلى شركائنا واستثمر في فرص تجارية حقيقية مع عوائد مجزية
          </p>
          <Button
            data-testid="cta-login-btn"
            onClick={() => navigate('/login')}
            size="lg"
            className="bg-accent hover:bg-accent/90 text-white rounded-full px-12 py-6 text-lg font-bold"
          >
            <Users className="ml-2 h-5 w-5" />
            دخول المنصة
            <ArrowRight className="mr-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold mb-2">شركة أفيوس للتجارة والتوريدات العامة</h3>
          <p className="text-stone-400">Avious for General Trading & Supplies</p>
          <p className="text-sm text-stone-500 mt-4">جميع الحقوق محفوظة © {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
}
